import PropTypes from 'prop-types';
import { Badge, Icon } from '@openedx/paragon';
import {
  RiArrowRightSLine,
  RiBox3Line,
  RiBugLine,
  RiQuestionLine,
} from '@remixicon/react';
import { useIntl } from '@edx/frontend-platform/i18n';
import { useNavigate } from 'react-router-dom';

import {
  formatTicketShortId,
  formatTimeAgo,
  getStatusBadgeColor,
  getStatusLabel,
  getTypeIconColor,
  getTypeLabel,
  isActiveStatus,
} from '../utils';
import messages from '../messages';

const TYPE_ICONS = {
  bug: RiBugLine,
  issue: RiQuestionLine,
  feature: RiBox3Line,
  other: RiBox3Line,
};

const getTypeIcon = (type) => TYPE_ICONS[(type || '').toLowerCase()] || RiBox3Line;

const ActiveTicketsRow = ({ tickets }) => {
  const intl = useIntl();
  const navigate = useNavigate();

  const activeTickets = tickets.filter((t) => isActiveStatus(t.status));
  if (activeTickets.length === 0) { return null; }

  return (
    <section className="learner-support__active">
      <h2 className="learner-support__section-title">
        {intl.formatMessage(messages.activeTicketsTitle)}
      </h2>
      <div className="learner-support__active-grid">
        {activeTickets.map((ticket) => {
          const TypeIcon = getTypeIcon(ticket.type);
          return (
            <button
              key={ticket.id}
              type="button"
              className="learner-support__active-card"
              onClick={() => navigate(`/support/${ticket.id}`)}
            >
              <div className="learner-support__active-card-header">
                <span className="learner-support__ticket-id">
                  {formatTicketShortId(ticket.id)}
                </span>
                <Badge variant="soft" color={getStatusBadgeColor(ticket.status)}>
                  {getStatusLabel(ticket.status, intl, messages)}
                </Badge>
              </div>
              <h3 className="learner-support__active-card-title">{ticket.title}</h3>
              <p className="learner-support__active-card-description">
                {ticket.description}
              </p>
              <div className="learner-support__active-card-footer">
                <span
                  className="learner-support__active-card-type"
                  style={{ color: getTypeIconColor(ticket.type) }}
                >
                  <Icon src={TypeIcon} size="sm" />
                  <span className="learner-support__active-card-type-label">
                    {getTypeLabel(ticket.type, intl, messages) || '—'}
                  </span>
                </span>
                <span className="learner-support__active-card-updated">
                  {intl.formatMessage(messages.updatedAgo, {
                    time: formatTimeAgo(ticket.updatedAt || ticket.createdAt, intl.locale),
                  })}
                  <Icon src={RiArrowRightSLine} size="xs" />
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
};

ActiveTicketsRow.propTypes = {
  tickets: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string,
      type: PropTypes.string,
      status: PropTypes.string.isRequired,
      createdAt: PropTypes.string.isRequired,
      updatedAt: PropTypes.string,
    }),
  ).isRequired,
};

export default ActiveTicketsRow;
